/**
 * Filter op één model, per pagina (04-10-2026, Jordy).
 *
 * - "All models" of één model (een persoon: FC en F2F samen).
 * - De keuze staat in het adres (`?model=rozy`), zodat hij een herlaadbeurt
 *   overleeft en je hem kunt doorsturen. Hij gaat NIET mee naar een andere
 *   pagina (keuze Jordy): elke pagina begint op "All models".
 * - Het filter geldt voor de hele pagina, ook voor de totalen erboven (keuze
 *   Jordy); de pagina gebruikt daarvoor `past()` of `slugs`.
 *
 * Gebruik:
 *   const filter = useModelFilter()
 *   <ModelFilter filter={filter} />
 *   rijen.filter((r) => filter.past(r.model_slug))
 */
import { useSearchParams } from 'react-router-dom'
import { useModellen, type Model } from '../lib/modellen'

export interface ModelFilterStaat {
  /** 'all' of de sleutel van het gekozen model. */
  waarde: string
  zet: (waarde: string) => void
  model: Model | undefined
  /** Hoort dit profiel (slug, id of naam) bij de keuze? Altijd true bij 'all'. */
  past: (sleutel: string | number | null | undefined) => boolean
  /** Slugs van het gekozen model, of null bij 'all' (voor een serverparameter). */
  slugs: string[] | null
  ids: number[] | null
  modellen: Model[]
  /**
   * Klaar voor achter een API-adres: '' bij "All models", anders
   * `&model=slug1,slug2`. Is de lijst nog niet binnen terwijl er wel een model
   * gekozen is, dan een filter dat niets raakt — nooit even "alles".
   */
  param: string
}

export function useModelFilter(): ModelFilterStaat {
  const [zoek, setZoek] = useSearchParams()
  const { modellen, modelVan } = useModellen()
  const waarde = (zoek.get('model') || 'all').toLowerCase()
  const model = waarde === 'all' ? undefined : modellen.find((m) => m.sleutel === waarde)
  const zet = (w: string) => {
    const nieuw = new URLSearchParams(zoek)
    if (w === 'all') nieuw.delete('model')
    else nieuw.set('model', w)
    setZoek(nieuw, { replace: true })
  }
  const past = (s: string | number | null | undefined) => {
    if (waarde === 'all') return true
    if (!model) return false
    const m = modelVan(s)
    if (m) return m.sleutel === model.sleutel
    return typeof s === 'string' && s.toLowerCase() === model.sleutel
  }
  const param = waarde === 'all' ? ''
    : `&model=${encodeURIComponent(model ? model.slugs.join(',') : '__laden__')}`
  return {
    waarde, zet, model, past, modellen, param,
    slugs: model ? model.slugs : null,
    ids: model ? model.ids : null,
  }
}

export function ModelFilter({ filter }: { filter: ModelFilterStaat }) {
  return (
    <select className="select" value={filter.waarde} aria-label="Filter by model"
            onChange={(e) => filter.zet(e.target.value)}>
      <option value="all">All models</option>
      {filter.modellen.map((m) => (
        <option key={m.sleutel} value={m.sleutel}>
          {m.naam}{m.fc && m.f2f ? ' (FC + F2F)' : m.f2f ? ' (F2F)' : ' (FC)'}
        </option>
      ))}
      {/* Een model uit het adres dat (nog) niet in de lijst staat, blijft zichtbaar. */}
      {filter.waarde !== 'all' && !filter.model ? <option value={filter.waarde}>{filter.waarde}</option> : null}
    </select>
  )
}
